import React, { FC } from 'react';
import {
  LazyLoadImage as BaseLazyLoadImage,
  LazyLoadImageProps,
} from 'react-lazy-load-image-component';

// Type workaround for React 18 compatibility with outdated @types/react-lazy-load-image-component
const LazyLoadImage = BaseLazyLoadImage as React.FC<LazyLoadImageProps>;

/**
 * Generates a PNG fallback path from a WebP image path
 * e.g., /images/uploads/crypto-coins.webp -> /images/uploads/crypto-coins.png
 */
const getFallbackSrc = (src: string): string => {
  if (src.endsWith('.webp')) {
    return src.replace(/\.webp$/, '.png');
  }
  return src;
};

interface ImageWithFallbackProps extends Omit<LazyLoadImageProps, 'src' | 'alt'> {
  src?: string | null;
  alt?: string | null;
}

/**
 * ImageWithFallback component that renders images with WebP format and PNG fallback.
 * If the image source ends with .webp, it wraps the image in a <picture> element
 * with a <source> for WebP and falls back to PNG for browsers that don't support WebP.
 */
const ImageWithFallback: FC<ImageWithFallbackProps> = ({
  src,
  alt,
  ...rest
}) => {
  if (!src) return null;

  const altText = alt ?? '';

  // If the source is a WebP image, use picture element with fallback
  if (src.endsWith('.webp')) {
    const fallbackSrc = getFallbackSrc(src);

    return (
      <picture>
        <source srcSet={src} type="image/webp" />
        <LazyLoadImage src={fallbackSrc} alt={altText} {...rest} />
      </picture>
    );
  }

  // For non-WebP images, render normally
  return <LazyLoadImage src={src} alt={altText} {...rest} />;
};

export default ImageWithFallback;

